import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Form,
  Space,
  Tooltip,
  Popconfirm,
  Tag,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, ReloadOutlined, BankOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import CBModal from '../../components/CBModal';
import { optionsPaysCedeao } from '../../variables/paysCedeao';
import PageHeader from '../../components/PageHeader';

const GREEN = '#1a6b3c';

export default function ParametresBanques() {
  const { t } = useTranslation('parametres');
  // Calculé au rendu, pas au chargement du module : les noms de pays suivront la langue.
  const PAYS_OPTIONS = optionsPaysCedeao();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [filtrePays, setFiltrePays] = useState(null);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/v1/banques');
      setData(res.data?.data || []);
    } catch { toast.error(i18n.t('parametres:banques.messages.erreurChargement')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return data
      .filter(b => !filtrePays || b.codePays === filtrePays)
      .filter(b => !search || [b.nom, b.code, b.codeBic, b.agence, b.ville]
        .some(v => v?.toLowerCase().includes(search.toLowerCase())));
  }, [data, search, filtrePays]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      nom:       record.nom,
      code:      record.code,
      codeBic:   record.codeBic,
      agence:    record.agence,
      ville:     record.ville,
      codePays:  record.codePays,
      telephone: record.telephone,
    });
    setModalOpen(true);
  };

  const onSubmit = async (vals) => {
    setSaving(true);
    try {
      const payload = {
        nom:       vals.nom?.trim(),
        code:      vals.code?.trim().toUpperCase(),
        codeBic:   vals.codeBic?.trim().toUpperCase() || null,
        agence:    vals.agence?.trim() || null,
        ville:     vals.ville?.trim() || null,
        codePays:  vals.codePays || null,
        telephone: vals.telephone?.trim() || null,
      };
      if (editing) {
        await axiosInstance.put(`/v1/banques/${editing.banqueTrackingId}`, payload);
        toast.success(t('banques.messages.banqueModifiee'));
      } else {
        await axiosInstance.post('/v1/banques', payload);
        toast.success(t('banques.messages.banqueCreee'));
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('banques.messages.erreurSauvegarde'));
    } finally { setSaving(false); }
  };

  const supprimer = async (record) => {
    try {
      await axiosInstance.delete(`/v1/banques/${record.banqueTrackingId}`);
      toast.success(t('banques.messages.banqueSupprimee'));
      load();
    } catch { toast.error(t('banques.messages.erreurSuppression')); }
  };

  const cols = [
    {
      title: t('banques.colonnes.banque'), key: 'nom', width: 280,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{r.nom}</div>
          {r.agence && <div style={{ fontSize: 12, color: '#6b7280' }}>{r.agence}</div>}
        </div>
      ),
    },
    {
      title: t('banques.colonnes.code'), dataIndex: 'code', key: 'code', width: 110,
      render: (v) => (
        <Tag style={{ fontFamily: 'var(--font-main)', fontWeight: 700, borderRadius: 6 }} color="geekblue">
          {v}
        </Tag>
      ),
    },
    {
      title: t('banques.colonnes.bic'), dataIndex: 'codeBic', key: 'bic', width: 120,
      render: (v) => v
        ? <span style={{ fontFamily: 'var(--font-main)', fontSize: 13, color: '#374151' }}>{v}</span>
        : <span style={{ color: '#d1d5db' }}>-</span>,
    },
    {
      title: t('banques.colonnes.ville'), dataIndex: 'ville', key: 'ville', width: 120,
      render: (v) => v || <span style={{ color: '#d1d5db' }}>-</span>,
    },
    {
      title: t('banques.colonnes.pays'), dataIndex: 'codePays', key: 'pays', width: 100,
      render: (v) => {
        const p = PAYS_OPTIONS.find(o => o.value === v);
        return p ? <Tag color="green" style={{ borderRadius: 6 }}>{p.label}</Tag>
                 : (v || <span style={{ color: '#d1d5db' }}>-</span>);
      },
    },
    {
      title: t('banques.colonnes.telephone'), dataIndex: 'telephone', key: 'tel', width: 140,
      render: (v) => v || <span style={{ color: '#d1d5db' }}>-</span>,
    },
    {
      title: '', key: 'actions', width: 90, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={t('banques.actions.modifier')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}
              style={{ borderRadius: 6 }} />
          </Tooltip>
          <Popconfirm title={t('banques.actions.supprimerConfirmation')} onConfirm={() => supprimer(r)}
            okText={t('banques.actions.oui')} cancelText={t('banques.actions.non')}>
            <Tooltip title={t('banques.actions.supprimer')}>
              <Button size="small" icon={<DeleteOutlined />} danger style={{ borderRadius: 6 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout title={t('banques.page.titreOnglet')}>
      <div>

        {/* Recherche et filtre pays sont appliques cote client : `filtered.length`
            correspond aux lignes affichees. */}
        <PageHeader
          titre={t('banques.page.titre')}
          icone={<BankOutlined />}
          compte={filtered.length}
          unite={t('banques.page.unite')}
          actions={(
            <Space>
              <Tooltip title={t('banques.actions.actualiser')}>
                <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }} />
              </Tooltip>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
                style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
                {t('banques.actions.ajouter')}
              </Button>
            </Space>
          )}
        />

        <div className="cb-card">
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <BankOutlined style={{ color: GREEN, fontSize: 18 }} />
            <Input
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              placeholder={t('banques.filtres.recherche')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 300, borderRadius: 8 }}
              allowClear
            />
            <Select
              placeholder={t('banques.filtres.pays')}
              allowClear
              value={filtrePays}
              onChange={setFiltrePays}
              style={{ width: 180, borderRadius: 8 }}
              options={PAYS_OPTIONS}
            />
          </div>

          <Table
            className="cb-table"
            dataSource={filtered.map((r, i) => ({ ...r, key: i }))}
            columns={cols}
            loading={loading}
            size="small"
            pagination={{ pageSize: 20, showTotal: (total) => t('banques.pagination.total', { count: total }) }}
          />
        </div>

        {/* Modal création / édition */}
        <CBModal
          title={
            <span style={{ fontSize: 16, fontWeight: 700 }}>
              {editing ? t('banques.modale.titreModification') : t('banques.modale.titreCreation')}
            </span>
          }
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          width={560}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 16 }}>
            <Form.Item name="nom" label={t('banques.modale.raisonSociale')}
              rules={[{ required: true, message: t('banques.modale.nomObligatoire') }]}>
              <Input placeholder={t('banques.modale.raisonSocialeExemple')} style={{ borderRadius: 8 }} />
            </Form.Item>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="code" label={t('banques.modale.codeInterne')}
                  rules={[
                    { required: true, message: t('banques.modale.codeObligatoire') },
                    { pattern: /^[A-Z0-9]{2,20}$/i, message: t('banques.modale.codeFormat') },
                  ]}>
                  <Input
                    placeholder={t('banques.modale.codeExemple')}
                    disabled={!!editing}
                    style={{ borderRadius: 8, fontFamily: 'var(--font-main)', textTransform: 'uppercase' }}
                    onChange={e => form.setFieldValue('code', e.target.value.toUpperCase())}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="codeBic" label={t('banques.modale.bic')}>
                  <Input
                    placeholder={t('banques.modale.bicExemple')}
                    style={{ borderRadius: 8, fontFamily: 'var(--font-main)', textTransform: 'uppercase' }}
                    onChange={e => form.setFieldValue('codeBic', e.target.value.toUpperCase())}
                    maxLength={15}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="agence" label={t('banques.modale.agence')}>
                  <Input placeholder={t('banques.modale.agenceExemple')} style={{ borderRadius: 8 }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="ville" label={t('banques.modale.ville')}>
                  <Input placeholder={t('banques.modale.villeExemple')} style={{ borderRadius: 8 }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="codePays" label={t('banques.modale.pays')}>
                  <Select
                    placeholder={t('banques.modale.paysPlaceholder')}
                    allowClear
                    style={{ borderRadius: 8 }}
                    options={PAYS_OPTIONS}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="telephone" label={t('banques.modale.telephone')}>
                  <Input placeholder={t('banques.modale.telephoneExemple')} style={{ borderRadius: 8 }} />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <Button onClick={() => setModalOpen(false)}>{t('banques.modale.annuler')}</Button>
              <Button type="primary" htmlType="submit" loading={saving}
                style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
                {editing ? t('banques.modale.enregistrer') : t('banques.modale.creer')}
              </Button>
            </div>
          </Form>
        </CBModal>
      </div>
    </Layout>
  );
}
